// Shared helpers for the Nuitee hotel flow.
//
// When a client's workflow requires approval and the trip is not approved yet, staff can still
// search and choose a room — but the flow SAVES the choice to the accommodation sector instead of
// booking it, so the hotel, dates and gross price are part of what the approver reviews. Booking
// unlocks once the trip is approved. A saved option is only a snapshot: rates and availability
// must be re-queried live before it can be booked.

// Does this client's workflow require approval for this trip type?
// Mirrors the per-trip-type override logic used when submitting a trip.
import { BOOKING_URL_KEYS } from './bookingContext';

/**
 * Where the LiteAPI payment wrapper should send the browser back to.
 *
 * ⚠️ NEVER build this from `window.location.href`. The booking popup carries its whole context on
 * the query string — for flights that includes every passenger's profile, identity documents and
 * loyalty numbers as JSON — and the wrapper rejects anything over 2048 characters with
 * "Invalid URL: URL must be 2048 characters or less". It fails at the pay button, after the fare
 * is held, which is the worst possible moment.
 *
 * None of that context is needed on the way back. The booking is already made by then; the page
 * reloads, recovers the finished payload from sessionStorage, confirms, and shows the outcome.
 * Only the handful of values the reloaded page cannot rebuild are carried across.
 */
export function paymentReturnUrl(extraParams = {}) {
  const KEEP = BOOKING_URL_KEYS;
  const current = new URLSearchParams(window.location.search);
  const url = new URL(window.location.origin + window.location.pathname);
  for (const key of KEEP) {
    const value = current.get(key);
    if (value !== null && value !== '') url.searchParams.set(key, value);
  }
  for (const [key, value] of Object.entries(extraParams)) url.searchParams.set(key, value);
  return url.toString();
}

export function tripRequiresApproval(clientConfig, tripType) {
  const byType = clientConfig?.workflow?.approvalByTripType;
  return (byType && tripType in byType)
    ? !!byType[tripType]
    : clientConfig?.workflow?.requiresApproval !== false;
}

// 'book'  — run the full flow through prebook + payment
// 'quote' — search and select only; the choice is saved to the trip for approval
export function hotelFlowMode(tripStatus, requiresApproval) {
  if (['approved', 'booked'].includes(tripStatus)) return 'book';
  if (!requiresApproval) return 'book';
  return 'quote';
}

// The reporting city drives hotel-spend and travel-policy reporting, so it must never end up
// holding the hotel's own name — a city that equals the property name is not a city, it is the
// search term leaking through, and it shows up in reports as a bogus destination.
function cityForSector(city, propertyName) {
  const c = (city || '').trim();
  if (!c) return '';
  return c.toLowerCase() === (propertyName || '').trim().toLowerCase() ? '' : c;
}

// Headline fields are mirrored onto the sector itself so the trip total — and therefore the
// approval request — includes the hotel cost. `bookingRef` is deliberately NOT set: its absence
// alongside a `proposedHotel` is what distinguishes a saved option from a confirmed booking.
export function sectorPatchFromOption(sector, o) {
  const propertyName = o.propertyName || sector.propertyName || '';
  return {
    ...sector,
    propertyName,
    checkIn:       o.checkIn       || sector.checkIn       || '',
    checkOut:      o.checkOut      || sector.checkOut      || '',
    cost:          o.price != null ? String(o.price) : (sector.cost || ''),
    roomType:      o.roomName      || sector.roomType      || '',
    inclusions:    o.boardName     || sector.inclusions    || '',
    international: o.international ?? sector.international ?? false,
    reportingCity: cityForSector(o.hotelCity || sector.reportingCity, propertyName),
    hotelId:       o.hotelId       || sector.hotelId       || '',
    proposedHotel: o,
  };
}

// A confirmed booking supersedes any saved option — clear it so the sector shows one state only.
export function sectorPatchFromBooking(sector, b) {
  const propertyName = b.propertyName || sector.propertyName || '';
  return {
    ...sector,
    propertyName,
    bookingRef:    b.bookingRef    || sector.bookingRef    || '',
    checkIn:       b.checkIn       || sector.checkIn       || '',
    checkOut:      b.checkOut      || sector.checkOut      || '',
    cost:          b.cost          || sector.cost          || '',
    roomType:      b.roomType      || sector.roomType      || '',
    inclusions:    b.inclusions    || sector.inclusions    || '',
    notes:         b.notes         || sector.notes         || '',
    international: b.international ?? sector.international ?? false,
    reportingCity: cityForSector(b.hotelCity || sector.reportingCity, propertyName),
    proposedHotel: null,
  };
}

// A saved option still awaiting booking — drives the "pending approval" UI on the sector.
export function hasPendingOption(sector) {
  return !!sector?.proposedHotel && !sector?.bookingRef;
}

// Trimmed payload handed back to the flow when re-opening a saved option, so it can restore the
// search and compare the saved price against live rates. Kept small — it travels as a URL param.
// Percent-encoding is left to URLSearchParams on both sides; encoding here too would double it.
export function encodeSavedOption(o) {
  if (!o) return '';
  const slim = {
    hotelId:      o.hotelId,
    propertyName: o.propertyName,
    checkIn:      o.checkIn,
    checkOut:     o.checkOut,
    adults:       o.adults,
    children:     o.children,
    currency:     o.currency,
    price:        o.price,
    roomName:     o.roomName,
    boardName:    o.boardName,
    mappedRoomId: o.mappedRoomId,
    isRefundable: o.isRefundable,
    savedAt:      o.savedAt,
  };
  try { return JSON.stringify(slim); } catch { return ''; }
}

export function decodeSavedOption(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
